import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

interface AttendeeReportSectionProps {
  title: string;
  count: number;
  /** Optional subtitle / explanation rendered under the title row. */
  subtitle?: ReactNode;
  children: ReactNode;
}

/**
 * Section wrapper used by every block in the Event Experience Report.
 * Mirrors the look of `FormSection` but adds a count chip in the title
 * row so the organizer can see at a glance how many items the section
 * contains.
 */
export function AttendeeReportSection({
  title,
  count,
  subtitle,
  children,
}: AttendeeReportSectionProps) {
  return (
    <Box sx={{ mb: 4 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: subtitle ? 0.5 : 1 }}
      >
        <Typography variant="h6" component="h2">
          {title}
        </Typography>
        <Chip label={count} size="small" />
      </Stack>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      <Divider sx={{ mb: 2 }} />
      {children}
    </Box>
  );
}
